import { AnalyticsEventName } from '@dupe/common/constants'
import {
  RecaptchaVerifier,
  linkWithPhoneNumber,
  signInWithPhoneNumber,
} from 'firebase/auth'
import { getAuth } from 'firebase/auth'
import type { E164Number } from 'libphonenumber-js/core'
import { Dispatch, SetStateAction } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { formatPhoneNumberIntl } from 'react-phone-number-input'
import PhoneInputWrapper from '~/src/components/PhoneInputWrapper'
import ProviderButton from '~/src/components/ProviderButton'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthCallback from '~/src/hooks/useAuthCallback'
import { RECAPTCA_ID } from '~/src/lib/constants'
import app from '~/src/lib/firebase/firebaseApp'
import {
  AppleProvider,
  GoogleAuthProvider,
} from '~/src/lib/firebase/firebaseAuth'
import logger from '~/src/lib/logger'

interface Props {
  phoneNumber: E164Number
  validNumber: boolean
  processing: boolean
  recaptchaVerifier: RecaptchaVerifier | undefined
  setRecaptchaVerifier: Dispatch<SetStateAction<RecaptchaVerifier | undefined>>
  setErrorMessage: (errorMessage: string | null) => void
  setProcessing: (processing: boolean) => void
  setValidNumber: (validNumber: boolean) => void
  setPhoneNumber: (phoneNumber: E164Number) => void
  setVerificationId: (verificationId: string) => void
}

const auth = getAuth(app)

const AuthProviderButtons: React.FC<Props> = ({
  phoneNumber,
  validNumber,
  processing,
  recaptchaVerifier,
  setRecaptchaVerifier,
  setErrorMessage,
  setProcessing,
  setValidNumber,
  setPhoneNumber,
  setVerificationId,
}) => {
  const analytics = useAnalytics()
  const { loading, authCallback, pendingEmailAuth } = useAuthCallback()

  const handleSendCode = async (type: 'sign-in' | 'auth-link' = 'sign-in') => {
    setErrorMessage(null)
    setProcessing(true)

    analytics.track(AnalyticsEventName.AuthModalSendCode, {
      type,
    })

    if (!validNumber) {
      const valid = isValidPhoneNumber(formatPhoneNumberIntl(phoneNumber))

      if (!valid) {
        setErrorMessage('Invalid phone number')
        setProcessing(false)
        return
      }
    }

    const phoneNumberFormatted = formatPhoneNumberIntl(phoneNumber)
    const valid = isValidPhoneNumber(phoneNumber)
    setValidNumber(valid)

    const appVerifier = !!recaptchaVerifier
      ? recaptchaVerifier
      : new RecaptchaVerifier(auth, RECAPTCA_ID, {
          size: 'invisible',
        })
    setRecaptchaVerifier(appVerifier)

    if (!appVerifier) {
      logger.debug('[AuthModal] - Error creating recaptcha verifier')

      analytics.track(AnalyticsEventName.AuthModalSendCodeError, {
        type,
        recaptchaVerifier,
      })

      setErrorMessage('Error sending SMS code')
      setProcessing(false)

      return
    }

    try {
      if (type === 'auth-link') {
        if (!auth.currentUser) {
          setErrorMessage('Error linking phone number')
          setProcessing(false)
          return
        }

        const result = await linkWithPhoneNumber(
          auth.currentUser,
          phoneNumberFormatted,
          appVerifier
        )

        setVerificationId(result.verificationId)
      } else if (type === 'sign-in') {
        const confirmationResult = await signInWithPhoneNumber(
          auth,
          phoneNumberFormatted,
          appVerifier
        )

        setVerificationId(confirmationResult.verificationId)
      }
      setProcessing(false)
    } catch (error) {
      // Handle error
      setErrorMessage('Error sending SMS code')
      setProcessing(false)
      logger.error('Error sending SMS:', { error })

      analytics.track(AnalyticsEventName.AuthModalSendCodeError, {
        type,
        error,
      })
    }
  }

  return (
    <div className='space-y-2'>
      <ProviderButton
        providerId={GoogleAuthProvider.PROVIDER_ID}
        onClick={authCallback(GoogleAuthProvider.PROVIDER_ID)}
        loading={loading}
        hideOtherButtons={pendingEmailAuth}
      >
        Continue with Google
      </ProviderButton>
      <ProviderButton
        providerId={AppleProvider.PROVIDER_ID}
        onClick={authCallback(AppleProvider.PROVIDER_ID)}
        loading={loading}
        hideOtherButtons={pendingEmailAuth}
      >
        Continue with Apple
      </ProviderButton>
      <PhoneInputWrapper
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        onSubmit={() => handleSendCode('sign-in')}
        processing={processing}
        validNumber={validNumber}
      />

      {/* <ProviderButton
          providerId={EmailAuthProvider.PROVIDER_ID}
          onClick={authCallback(EmailAuthProvider.PROVIDER_ID)}
          loading={loading}
          pendingEmailAuth={pendingEmailAuth}
          clearPendingEmailAuth={clearPendingEmailAuth}
        >
          Use email
        </ProviderButton> */}
    </div>
  )
}

export default AuthProviderButtons
